import dayjs from "dayjs";
import { UseFormReset, UseFormWatch } from "react-hook-form";
import { UtilityBillFormData } from "types";
import { CollectorDTO, UtilityBillByIdDTO, DocumentByIdDTO } from "api";
import { QUERY_KEYS } from "consts";
import { extractErrorMessage } from "utils";
import { sendUtilityBillNotificationMessage } from "pages/authenticated/documents/utils";
import {
  areDatesInvalid,
  getCreateBillInDocumentDTO,
  getUtilityBillUpdateDTO,
} from "../utils";

interface UseFormHandlersParams {
  isCreateMode: boolean;
  document?: DocumentByIdDTO;
  utilityBill?: UtilityBillByIdDTO;
  collectorsFromSiteData?: CollectorDTO[];
  queryClient: ReturnType<typeof import("@tanstack/react-query").useQueryClient>;
  notify: ReturnType<typeof import("hooks").useNotify>;
  reset: UseFormReset<UtilityBillFormData>;
  watch: UseFormWatch<UtilityBillFormData>;
  setNewCollectorId: (id?: number) => void;
  setIsManualApproveDisabled: (disabled: boolean) => void;
  closeSaveChangesDialog: () => void;
  buttonName: string;
  setButtonName: (name: string) => void;
  trackEvent: (eventName: string, pageTitle: string) => void;
  onClose?: () => void;
  manuallyApprove: (id: string, options?: any) => Promise<any>;
  createBill: (params: any, options?: any) => Promise<any>;
  update: (params: any, options?: any) => Promise<any>;
}

export const createFormHandlers = ({
  isCreateMode,
  document,
  utilityBill,
  collectorsFromSiteData,
  queryClient,
  notify,
  reset,
  watch,
  setNewCollectorId,
  setIsManualApproveDisabled,
  closeSaveChangesDialog,
  buttonName,
  setButtonName,
  trackEvent,
  onClose,
  manuallyApprove,
  createBill,
  update,
}: UseFormHandlersParams) => {
  const handleSaveClick = async (formData: UtilityBillFormData) => {
    if (areDatesInvalid(formData.startDate, formData.endDate)) {
      notify.error(
        "Invalid Date Entry: The start date cannot be later than the end date. Please review and correct the dates you've entered.",
      );
      return;
    }

    if (isCreateMode) {
      if (!document?.id) {
        notify.error("Document not found");
        return;
      }
      const startDate = dayjs(formData.startDate);
      const endDate = dayjs(formData.endDate);

      if (!startDate.isValid() || !endDate.isValid()) {
        notify.error("Invalid date format");
        return;
      }

      const modifiedFormData = {
        ...formData,
        startDate,
        endDate,
      };

      try {
        await createBill(
          {
            documentId: document.id,
            body: getCreateBillInDocumentDTO(modifiedFormData, collectorsFromSiteData),
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries([QUERY_KEYS.DOCUMENTS]);
              sendUtilityBillNotificationMessage(
                notify.success,
                "Successfully saved bill into",
                document.filename,
              );
              onClose?.();
            },
          })
      } catch (error: unknown) {
        sendUtilityBillNotificationMessage(
          notify.error,
          extractErrorMessage(error, "Failed to save bill"),
          document.filename,
        );
      }
      return;
    }

    // Edit mode
    if (!utilityBill?.id) {
      notify.error("Utility bill not found");
      return;
    }
    if (buttonName === "Confirm") {
      try {
        await manuallyApprove(utilityBill.id);
        queryClient.invalidateQueries([
          QUERY_KEYS.UTILITY_BILLS,
          utilityBill.id,
        ]);
        closeSaveChangesDialog();
        sendUtilityBillNotificationMessage(
          notify.success,
          "Successfully manually approved utility bill",
          utilityBill.filename,
        );
      } catch (error: unknown) {
        sendUtilityBillNotificationMessage(
          notify.error,
          extractErrorMessage(error, "Failed to manually approve utility bill"),
          utilityBill.filename,
        );
      }
    } else {
      try {
        const watchValues = watch();
        const response = await update(
          getUtilityBillUpdateDTO(
            utilityBill.id,
            formData,
            collectorsFromSiteData,
          ),
        );
        queryClient.setQueryData([
          QUERY_KEYS.UTILITY_BILLS,
          utilityBill.id,
        ], response);
        setIsManualApproveDisabled(false);
        closeSaveChangesDialog();
        reset(watchValues);
        if (setNewCollectorId) {
          setNewCollectorId(undefined);
        }
        sendUtilityBillNotificationMessage(
          notify.success,
          "Successfully saved utility bill",
          utilityBill.filename,
        );
      } catch (error: unknown) {
        notify.error(extractErrorMessage(error, "Failed to save utility bill"));
      }
    }
    trackEvent("Edited Data Field", "Document view - Invoice Data");
    setButtonName("");
  };

  const handleManualApproveClick = async () => {
    if (!utilityBill) return;
    await manuallyApprove(utilityBill.id, {
      onSuccess: () => {
        queryClient.invalidateQueries([
          QUERY_KEYS.UTILITY_BILLS,
          utilityBill.id,
        ]);
        sendUtilityBillNotificationMessage(
          notify.success,
          "Successfully manually approved utility bill",
          utilityBill.filename,
        );
      },
      onError: (error: unknown) => {
        const errorMessage = extractErrorMessage(error, "Failed to manually approve utility bill");
        sendUtilityBillNotificationMessage(
          notify.error,
          errorMessage,
          utilityBill.filename,
        );
      }
    });
    setButtonName("");
  };

  return {
    handleSaveClick,
    handleManualApproveClick,
  };
};

